import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { productApis } from '../../../../gateways/breadstack/productApis'
import {
  usePantryStore,
  usePantryWarehouses,
} from '../../../../providers/PantryProvider'
import type { LoyaltyCouponProductRef } from '../../../../types/loyaltyCoupon'
import type { StoreType } from '../../../../types/product'

const SUPPORTED_STORE_TYPES: StoreType[] = [
  'cova',
  'greenline',
  'lightspeed',
  'flowhub',
]

const isSupported = (storeType: string | undefined): storeType is StoreType =>
  Boolean(storeType) && SUPPORTED_STORE_TYPES.includes(storeType as StoreType)

interface UseCouponProductSearchOptions {
  /**
   * Already-picked product IDs that should be removed from the dropdown so
   * the user never sees the same product twice. Passing this in to the hook
   * (rather than filtering inside the component) keeps the displayed result
   * count stable — the limit slice happens *after* exclusion.
   */
  excludeIds?: readonly string[]
}

/**
 * Debounced product search powering the "Specific Products" mode of the
 * coupon modal. Consumes the Product module gateway's variation-aware
 * `searchProductsWithVariations` helper so each picked product carries the
 * `type` + `variations` metadata the Cova coupon `applied_products` payload
 * requires. Falls back to an empty result list for store types that don't
 * expose a product list (e.g. shopify, woocommerce).
 */
export const useCouponProductSearch = (
  query: string,
  options: UseCouponProductSearchOptions = {},
): { results: LoyaltyCouponProductRef[]; isLoading: boolean } => {
  const store = usePantryStore()
  const warehouses = usePantryWarehouses()
  const storeId = store?.storeId ?? ''
  const storeType = store?.storeType
  const trimmed = query.trim()
  const supported = isSupported(storeType)
  const { excludeIds } = options

  // Cova's `/products/variations` endpoint filters by `location_ids[]`. We
  // pull every active warehouse from the pantry context so the search
  // returns products available in any store location — the user later picks
  // which products the coupon actually applies to.
  const locationIds = useMemo(
    () => warehouses.map((w) => w.id).filter((id): id is string => Boolean(id)),
    [warehouses],
  )

  const { data, isFetching } = useQuery({
    queryKey: [
      'loyalty',
      'coupons',
      'product-search',
      storeType,
      storeId,
      trimmed,
      locationIds,
    ],
    queryFn: async () => {
      const items = await productApis.searchProductsWithVariations(
        storeId,
        storeType as StoreType,
        { search: trimmed, locationIds },
      )
      return items.map<LoyaltyCouponProductRef>((p) => ({
        id: p.id,
        name: p.name,
        type: p.type,
        variations: p.variations,
      }))
    },
    enabled: Boolean(storeId) && supported && trimmed.length > 0,
    staleTime: 30 * 1000,
  })

  // Exclude already-picked products from the dropdown. We rebuild the Set
  // each render via `useMemo` so the filter reflects the latest pick state
  // without paying for re-allocation on every keystroke.
  const excludeSet = useMemo(() => new Set(excludeIds ?? []), [excludeIds])
  const results = useMemo(
    () => (data ?? []).filter((r) => !excludeSet.has(r.id)),
    [data, excludeSet],
  )

  return { results, isLoading: isFetching }
}
